// PWA install helpers. Browsers fire `beforeinstallprompt` once, early — so we
// capture and defer it at module load, then expose a one-touch install later
// (Chrome/Edge/Android). iOS Safari has no programmatic install, so we detect it
// and the UI shows Add-to-Home-Screen guidance instead. All best-effort: where
// install isn't available these simply report so and the UI adapts.

let deferredPrompt = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferredPrompt = e; emit(); });
  window.addEventListener('appinstalled', () => { deferredPrompt = null; emit(); });
}

// Already running as an installed app (standalone display mode / iOS home-screen)?
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return !!(window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone);
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

// A phone/tablet (vs a computer)? Installing is a DIFFERENT thing on each — a
// home-screen app on mobile, a windowed desktop app on a computer — so install
// UI uses this to say which one the button will actually give you. UA-based on
// purpose: a narrow desktop window is still a computer.
export function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

// Can we show a native install prompt right now?
export function canInstallApp() { return !!deferredPrompt; }

// Subscribe to install-availability changes (prompt captured, or app installed).
export function onInstallChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// Trigger the native install prompt. Returns 'installed' | 'dismissed' | 'unavailable'.
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  const e = deferredPrompt;
  deferredPrompt = null; // a prompt can only be used once
  e.prompt();
  let outcome = 'dismissed';
  try { ({ outcome } = await e.userChoice); } catch { /* ignore */ }
  emit();
  return outcome === 'accepted' ? 'installed' : 'dismissed';
}
